import React, { useState, useEffect } from 'react';
import { promocodesAPI, catalogAPI, placesAPI } from '../services/api';
import { Plus, Trash2, Tag, ChevronDown, ChevronRight, Edit2, FolderPlus } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import toast from 'react-hot-toast';

const PER_PAGE = 50;

function PromoForm({ initial, onSubmit, onClose, groups, places }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: '', description: '', discount_type: 'percent', discount_value: '',
    min_order_amount: 0, max_discount: '', max_uses: '', max_uses_per_client: 1,
    allow_with_bonus: true, valid_from: '', valid_until: '',
    restriction_mode: 'all', purchase_condition: 'any', group_id: '',
    ...(initial || {}),
  });
  const [allowedProducts, setAllowedProducts] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [allowedPlaces, setAllowedPlaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    catalogAPI.categories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      catalogAPI.products(0, 50, null, productSearch || null, false)
        .then(r => setProducts(r.data?.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);


  useEffect(() => {
    if (initial?.allowed_place_ids?.length) {
      setAllowedPlaces(places.filter(p => initial.allowed_place_ids.includes(String(p.id))));
    }
  }, [initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code) { toast.error('Введите код'); return; }
    if (!form.discount_value) { toast.error('Введите скидку'); return; }
    setLoading(true);
    try {
      const data = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: parseFloat(form.min_order_amount) || 0,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_client: parseInt(form.max_uses_per_client) || 1,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        group_id: form.group_id || null,
        allowed_product_ids: form.restriction_mode === 'products' ? allowedProducts.map(p => String(p.id)) : null,
        allowed_category_ids: form.restriction_mode === 'categories' ? allowedCategories.map(c => String(c.id)) : null,
        allowed_place_ids: allowedPlaces.length > 0 ? allowedPlaces.map(p => String(p.id)) : null,
      };
      await onSubmit(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 className="modal-title">{isEdit ? 'Редактировать' : 'Новый промокод'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Код *</label>
              <input className="form-input" value={form.code} disabled={isEdit}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME" />
            </div>
            <div className="form-group">
              <label className="form-label">Группа</label>
              <select className="form-input" value={form.group_id || ''} onChange={e => setForm({ ...form, group_id: e.target.value })}>
                <option value="">Без группы</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Описание</label>
            <input className="form-input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Тип скидки</label>
              <select className="form-input" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}>
                <option value="percent">Процент</option>
                <option value="fixed">Фиксированная</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Размер скидки *</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.discount_value}
                onChange={e => setForm({ ...form, discount_value: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Мин. сумма</label>
              <input className="form-input" type="number" min="0" value={form.min_order_amount}
                onChange={e => setForm({ ...form, min_order_amount: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Макс. скидка</label>
              <input className="form-input" type="number" min="0" value={form.max_discount || ''}
                onChange={e => setForm({ ...form, max_discount: e.target.value })} placeholder="∞" />
            </div>
            <div className="form-group">
              <label className="form-label">Макс. исп.</label>
              <input className="form-input" type="number" min="1" value={form.max_uses || ''}
                onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" />
            </div>
          </div>


          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Условие покупки</label>
              <select className="form-input" value={form.purchase_condition} onChange={e => setForm({ ...form, purchase_condition: e.target.value })}>
                <option value="any">Любая покупка</option>
                <option value="first">Только первая покупка</option>
                <option value="one">Одноразовый на клиента</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Исп. на клиента</label>
              <input className="form-input" type="number" min="1" value={form.max_uses_per_client}
                onChange={e => setForm({ ...form, max_uses_per_client: e.target.value })} />
            </div>
          </div>


          <div className="form-group">
            <label className="form-label">Точки продаж (пусто = все)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {allowedPlaces.map(p => (
                <span key={p.id} className="badge badge-info" style={{ cursor: 'pointer' }}
                  onClick={() => setAllowedPlaces(allowedPlaces.filter(x => x.id !== p.id))}>
                  {p.name} ✕
                </span>
              ))}
            </div>
            <select className="form-input" value="" onChange={e => {
              const place = places.find(p => p.id === e.target.value);
              if (place && !allowedPlaces.find(p => p.id === place.id)) {
                setAllowedPlaces([...allowedPlaces, place]);
              }
            }}>
              <option value="">+ Добавить точку</option>
              {places.filter(p => !allowedPlaces.find(ap => ap.id === p.id)).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>


          <div className="form-group">
            <label className="form-label">Ограничение по товарам</label>
            <select className="form-input" value={form.restriction_mode} onChange={e => setForm({ ...form, restriction_mode: e.target.value })}>
              <option value="all">Все товары</option>
              <option value="products">Только определённые товары</option>
              <option value="categories">Только определённые категории</option>
            </select>
          </div>

          {form.restriction_mode === 'products' && (
            <div className="form-group">
              <label className="form-label">Товары</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {allowedProducts.map(p => (
                  <span key={p.id} className="badge badge-info" style={{ cursor: 'pointer', fontSize: 11 }}
                    onClick={() => setAllowedProducts(allowedProducts.filter(x => x.id !== p.id))}>
                    {p.name} ✕
                  </span>
                ))}
              </div>
              <SearchableSelect
                items={products.map(p => ({ value: p.id, label: p.name }))}
                onSelect={item => {
                  if (!allowedProducts.find(p => p.id === item.value))
                    setAllowedProducts([...allowedProducts, { id: item.value, name: item.label }]);
                }}
                onSearch={setProductSearch}
                placeholder="Поиск товаров..."
              />
            </div>
          )}

          {form.restriction_mode === 'categories' && (
            <div className="form-group">
              <label className="form-label">Категории</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {allowedCategories.map(c => (
                  <span key={c.id} className="badge badge-info" style={{ cursor: 'pointer', fontSize: 11 }}
                    onClick={() => setAllowedCategories(allowedCategories.filter(x => x.id !== c.id))}>
                    {c.name} ✕
                  </span>
                ))}
              </div>
              <select className="form-input" value="" onChange={e => {
                const cat = categories.find(c => String(c.id) === e.target.value);
                if (cat && !allowedCategories.find(c => c.id === cat.id))
                  setAllowedCategories([...allowedCategories, cat]);
              }}>
                <option value="">+ Добавить категорию</option>
                {categories.filter(c => !allowedCategories.find(ac => ac.id === c.id)).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Действует с</label>
              <input className="form-input" type="datetime-local" value={form.valid_from || ''}
                onChange={e => setForm({ ...form, valid_from: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Действует до</label>
              <input className="form-input" type="datetime-local" value={form.valid_until || ''}
                onChange={e => setForm({ ...form, valid_until: e.target.value })} />
            </div>
          </div>

          <label className="form-check" style={{ marginBottom: 16 }}>
            <input type="checkbox" checked={form.allow_with_bonus} onChange={e => setForm({ ...form, allow_with_bonus: e.target.checked })} />
            Можно использовать с бонусами
          </label>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : isEdit ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const conditionLabel = { any: 'Любая', first: 'Первая покупка', one: 'Одноразовый' };

function PromoRow({ promo, onDelete, onEdit }) {
  return (
    <tr>
      <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{promo.code}</td>
      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{promo.description || '—'}</td>
      <td>{promo.discount_type === 'percent' ? `${promo.discount_value}%` : `${promo.discount_value}₽`}</td>
      <td style={{ fontSize: 12 }}>{conditionLabel[promo.purchase_condition] || '—'}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{promo.used_count}{promo.max_uses ? `/${promo.max_uses}` : ''}</td>
      <td><span className={`badge ${promo.is_active ? 'badge-success' : 'badge-danger'}`}>{promo.is_active ? 'Активен' : 'Выкл'}</span></td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => onEdit(promo)}><Edit2 size={14} /></button>
          <button className="btn btn-danger btn-sm" onClick={() => onDelete(promo.id)}><Trash2 size={14} /></button>
        </div>
      </td>
    </tr>
  );
}

export default function PromocodesPage() {
  const [promos, setPromos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [places, setPlaces] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const load = () => {
    promocodesAPI.list(0, 200).then(r => setPromos(r.data.items || [])).catch(() => {});
    promocodesAPI.groups().then(r => setGroups(r.data || [])).catch(() => {});
    placesAPI.list().then(r => setPlaces(r.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const toggleGroup = (id) => {
    const next = new Set(expandedGroups);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedGroups(next);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить промокод?')) return;
    try { await promocodesAPI.delete(id); toast.success('Удалено'); load(); }
    catch { toast.error('Ошибка'); }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await promocodesAPI.createGroup({ name: newGroupName.trim() });
      toast.success('Группа создана');
      setNewGroupName(''); setShowGroupCreate(false); load();
    } catch { toast.error('Ошибка'); }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Удалить группу? Промокоды будут откреплены.')) return;
    try { await promocodesAPI.deleteGroup(id); toast.success('Группа удалена'); load(); }
    catch { toast.error('Ошибка'); }
  };

  const ungrouped = promos.filter(p => !p.group_id);
  const grouped = groups.map(g => ({
    ...g,
    promos: promos.filter(p => p.group_id === g.id),
  }));

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Промокоды</h1>
        <p className="page-subtitle">{promos.length} промокодов, {groups.length} групп</p>
      </div>
      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => { setEditPromo(null); setShowCreate(true); }}>
          <Plus size={16} /> Промокод
        </button>
        <button className="btn btn-ghost" onClick={() => setShowGroupCreate(!showGroupCreate)}>
          <FolderPlus size={16} /> Группа
        </button>
      </div>

      {showGroupCreate && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          <input className="form-input" style={{ flex: 1 }} placeholder="Название группы..."
            value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateGroup()} />
          <button className="btn btn-primary btn-sm" onClick={handleCreateGroup}>Создать</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowGroupCreate(false)}>✕</button>
        </div>
      )}


      {grouped.map(g => (
        <div key={g.id} style={{ marginBottom: 12 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', cursor: 'pointer' }}
            onClick={() => toggleGroup(g.id)}
          >
            {expandedGroups.has(g.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Tag size={14} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 600, flex: 1 }}>{g.name}</span>
            <span className="badge badge-info">{g.promos.length}</span>
            <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleDeleteGroup(g.id); }}
              style={{ padding: 2 }}><Trash2 size={12} /></button>
          </div>
          {expandedGroups.has(g.id) && g.promos.length > 0 && (
            <div className="table-container" style={{ marginTop: 0, borderTop: 'none' }}>
              <table className="table">
                <thead><tr><th>Код</th><th>Описание</th><th>Скидка</th><th>Условие</th><th>Исп.</th><th>Статус</th><th></th></tr></thead>
                <tbody>
                  {g.promos.map(p => <PromoRow key={p.id} promo={p} onDelete={handleDelete} onEdit={p => { setEditPromo(p); setShowCreate(true); }} />)}
                </tbody>
              </table>
            </div>
          )}
          {expandedGroups.has(g.id) && g.promos.length === 0 && (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>Нет промокодов в группе</div>
          )}
        </div>
      ))}


      {ungrouped.length > 0 && (
        <>
          {groups.length > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, marginTop: 16 }}>Без группы</div>}
          <div className="table-container">
            <table className="table">
              <thead><tr><th>Код</th><th>Описание</th><th>Скидка</th><th>Условие</th><th>Исп.</th><th>Статус</th><th></th></tr></thead>
              <tbody>
                {ungrouped.map(p => <PromoRow key={p.id} promo={p} onDelete={handleDelete} onEdit={p => { setEditPromo(p); setShowCreate(true); }} />)}
              </tbody>
            </table>
          </div>
        </>
      )}

      {promos.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Промокодов нет</div>
      )}

      {showCreate && (
        <PromoForm
          initial={editPromo}
          groups={groups}
          places={places}
          onClose={() => { setShowCreate(false); setEditPromo(null); }}
          onSubmit={async (data) => {
            if (editPromo) {
              await promocodesAPI.update(editPromo.id, data);
              toast.success('Сохранено');
            } else {
              await promocodesAPI.create(data);
              toast.success('Промокод создан');
            }
            load();
          }}
        />
      )}
    </div>
  );
}